import React, { useEffect, useState } from "react";
import { api, fmt, erpExport } from "../../../services/erpApi";
import { PageHeader, LoadingScreen, Tabs, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Download, RefreshCw, FileSpreadsheet, FileText } from "lucide-react";

const inputStyle = { padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box" };

const PL = () => {
  const toast = useToast();
  const [from, setFrom] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [compare, setCompare] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await api.reports.profitLoss({ date_from: from, date_to: to, compare_prev: compare }));
    } catch (e) { toast(e.message || "Load failed", "error"); }
    setLoading(false);
  };

  const rows = (arr) => (arr || []).map((r) => ({ name: r.label, code: r.code, amount: Number(r.amount) || 0 }));

  return (
    <div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "20px" }}>
        <label style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>
          From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ ...inputStyle, marginTop: "4px", display: "block" }} />
        </label>
        <label style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>
          To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ ...inputStyle, marginTop: "4px", display: "block" }} />
        </label>
        <label style={{ fontSize: "13px", fontWeight: "600", color: "#334155", display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
          Compare previous period
        </label>
        <button onClick={load} disabled={loading} className="ess-btn ess-btn-primary">
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />} Load
        </button>
        <button onClick={() => erpExport({ kind: "profit-loss", format: "xlsx", date_from: from, date_to: to })} className="ess-btn ess-btn-secondary">
          <FileSpreadsheet size={14} /> XLSX
        </button>
        <button onClick={() => erpExport({ kind: "profit-loss", format: "pdf", date_from: from, date_to: to })} className="ess-btn ess-btn-secondary">
          <FileText size={14} /> PDF
        </button>
      </div>

      {loading ? <LoadingScreen /> : !data ? (
        <EmptyState icon="📊" title="Load the report" subtitle="Pick a period and press Load" />
      ) : (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "20px" }}>
            {[
              { l: "Revenue", v: data.revenue_total, c: "#16a34a" },
              { l: "Expenses", v: data.expense_total, c: "#e11d48" },
              { l: "COGS", v: data.cogs_total, c: "#d97706" },
              { l: "Gross Profit", v: data.gross_profit, c: "#0891b2" },
              { l: "Net Profit", v: data.net_profit, c: Number(data.net_profit) >= 0 ? "#16a34a" : "#e11d48" },
            ].map((k) => (
              <div key={k.l} style={{ background: "white", border: "1px solid #e2e7e2", borderRadius: "8px", padding: "16px" }}>
                <p style={{ fontSize: "12px", color: "#64748b", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.05em" }}>{k.l}</p>
                <p style={{ fontSize: "22px", fontWeight: "800", color: k.c, marginTop: "6px" }}>{fmt.moneyShort(k.v)}</p>
                <p style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>{fmt.money(k.v)}</p>
              </div>
            ))}
          </div>

          <Section title="Revenue" items={rows(data.revenue)} total={data.revenue_total} />
          <Section title="Expenses" items={rows(data.expenses)} total={data.expense_total} />

          {data.previous_period && (
            <div style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: "8px", padding: "14px 18px", marginTop: "16px", fontSize: "13px", color: "#475569" }}>
              <strong>Previous period:</strong> net profit {fmt.money(data.previous_period.net_profit)} (vs {fmt.money(data.net_profit)} this period)
            </div>
          )}
          <p style={{ fontSize: "12px", color: "#94a3b8", marginTop: "10px" }}>
            COGS is detected by heuristic (accounts 60% or names containing "achat").
          </p>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, items, total }) => (
  <div style={{ background: "white", border: "1px solid #e2e7e2", borderRadius: "8px", overflow: "hidden", marginBottom: "16px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 18px", borderBottom: "1px solid #e2e7e2", background: "#fafbfa" }}>
      <h4 style={{ fontSize: "14px", fontWeight: "700", color: "#1e293b", margin: 0 }}>{title}</h4>
      <span style={{ fontSize: "14px", fontWeight: "800", color: "#1e293b" }}>{fmt.money(total)}</span>
    </div>
    {items.length === 0 ? (
      <p style={{ padding: "16px 18px", fontSize: "13px", color: "#94a3b8" }}>No lines</p>
    ) : (
      <div className="overflow-x-auto">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {items.map((r, i) => (
              <tr key={i} className="ess-tr">
                <td className="ess-td" style={{ color: "#475569" }}>{r.name}</td>
                <td className="ess-td" style={{ color: "#64748b", fontFamily: "monospace" }}>{r.code}</td>
                <td className="ess-td" style={{ fontWeight: "600", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const BS = () => {
  const toast = useToast();
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await api.reports.balanceSheet({ as_of: asOf }));
    } catch (e) { toast(e.message || "Load failed", "error"); }
    setLoading(false);
  };

  const group = (type) => (data?.lines || []).filter((l) => l.account_type === type);
  const sum = (arr) => arr.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  return (
    <div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "20px" }}>
        <label style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>
          As of <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} style={{ ...inputStyle, marginTop: "4px", display: "block" }} />
        </label>
        <button onClick={load} disabled={loading} className="ess-btn ess-btn-primary">
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />} Load
        </button>
        <button onClick={() => erpExport({ kind: "balance-sheet", format: "xlsx", as_of: asOf })} className="ess-btn ess-btn-secondary">
          <FileSpreadsheet size={14} /> XLSX
        </button>
        <button onClick={() => erpExport({ kind: "balance-sheet", format: "pdf", as_of: asOf })} className="ess-btn ess-btn-secondary">
          <FileText size={14} /> PDF
        </button>
      </div>
      {loading ? <LoadingScreen /> : !data ? (
        <EmptyState icon="⚖️" title="Load the balance sheet" subtitle="Pick a date and press Load" />
      ) : (
        <div style={{ display: "grid", gap: "16px" }} className="ess-rg-3">
          {[
            { title: "Assets", rows: group("asset"), total: sum(group("asset")) },
            { title: "Liabilities", rows: group("liability"), total: sum(group("liability")) },
            { title: "Equity", rows: group("equity"), total: sum(group("equity")) },
          ].map((col) => (
            <div key={col.title} style={{ background: "white", border: "1px solid #e2e7e2", borderRadius: "8px", overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid #e2e7e2", background: "#fafbfa", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 style={{ fontSize: "14px", fontWeight: "700", color: "#1e293b", margin: 0 }}>{col.title}</h4>
                <span style={{ fontSize: "14px", fontWeight: "800", color: "#1e293b" }}>{fmt.moneyShort(col.total)}</span>
              </div>
              {col.rows.length === 0 ? (
                <p style={{ padding: "16px 18px", fontSize: "13px", color: "#94a3b8" }}>No lines</p>
              ) : (
                <div className="overflow-x-auto">
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      {col.rows.map((l, i) => (
                        <tr key={i} className="ess-tr">
                          <td className="ess-td" style={{ color: "#475569", fontFamily: "monospace" }}>{l.code} {l.name}</td>
                          <td className="ess-td" style={{ fontWeight: "600", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(l.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TX = () => {
  const toast = useToast();
  const [from, setFrom] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.reports.taxes({ date_from: from, date_to: to });
      setData(res?.data || []);
      setTotals(res?.totals || null);
    } catch (e) { toast(e.message || "Load failed", "error"); }
    setLoading(false);
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "20px" }}>
        <label style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>
          From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ ...inputStyle, marginTop: "4px", display: "block" }} />
        </label>
        <label style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>
          To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ ...inputStyle, marginTop: "4px", display: "block" }} />
        </label>
        <button onClick={load} disabled={loading} className="ess-btn ess-btn-primary">
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />} Load
        </button>
        <button onClick={() => erpExport({ kind: "taxes", format: "xlsx", date_from: from, date_to: to })} className="ess-btn ess-btn-secondary">
          <FileSpreadsheet size={14} /> XLSX
        </button>
      </div>
      {loading ? <LoadingScreen /> : !data ? (
        <EmptyState icon="🧾" title="Load taxes report" subtitle="Pick a period and press Load" />
      ) : (
        <div style={{ background: "white", border: "1px solid #e2e7e2", borderRadius: "8px", overflow: "hidden" }}>
          <div className="overflow-x-auto">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th className="ess-th">Tax</th>
                  <th className="ess-th ess-th-r">Base</th>
                  <th className="ess-th ess-th-r">Tax Amount</th>
                  <th className="ess-th ess-th-r">Invoices</th>
                </tr>
              </thead>
            <tbody>
              {(data || []).map((t, i) => (
                <tr key={t.tax_id || i} className="ess-tr">
                  <td className="ess-td" style={{ color: "#475569" }}>
                    <strong>{t.tax_name}</strong> <span style={{ color: "#94a3b8", fontFamily: "monospace" }}>{t.tax_code}</span>
                  </td>
                  <td className="ess-td" style={{ textAlign: "right", color: "#334155", whiteSpace: "nowrap" }}>{fmt.money(t.base_amount)}</td>
                  <td className="ess-td" style={{ textAlign: "right", fontWeight: "600", whiteSpace: "nowrap" }}>{fmt.money(t.tax_amount)}</td>
                  <td className="ess-td" style={{ textAlign: "right", color: "#64748b" }}>{t.count_invoices}</td>
                </tr>
              ))}
            </tbody>
            {totals && (
              <tfoot>
                <tr style={{ borderTop: "2px solid #e2e8f0", background: "#f8fafc" }}>
                  <td style={{ padding: "12px 18px", fontSize: "13px", fontWeight: "700", color: "#1e293b" }}>Totals</td>
                  <td style={{ padding: "12px 18px", fontSize: "13px", fontWeight: "700", color: "#1e293b", textAlign: "right" }}>{fmt.money(totals.base_amount)}</td>
                  <td style={{ padding: "12px 18px", fontSize: "13px", fontWeight: "700", color: "#1e293b", textAlign: "right" }}>{fmt.money(totals.tax_amount)}</td>
                  <td style={{ padding: "12px 18px", fontSize: "13px", fontWeight: "700", color: "#1e293b", textAlign: "right" }}>{totals.count_invoices}</td>
                </tr>
              </tfoot>
            )}
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

const SV = () => {
  const toast = useToast();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await api.reports.stockValuation({ date }));
    } catch (e) { toast(e.message || "Load failed", "error"); }
    setLoading(false);
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "20px" }}>
        <label style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>
          Date <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...inputStyle, marginTop: "4px", display: "block" }} />
        </label>
        <button onClick={load} disabled={loading} className="ess-btn ess-btn-primary">
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />} Load
        </button>
        <button onClick={() => erpExport({ kind: "stock-valuation", format: "xlsx", date })} className="ess-btn ess-btn-secondary">
          <FileSpreadsheet size={14} /> XLSX
        </button>
      </div>
      {loading ? <LoadingScreen /> : !data ? (
        <EmptyState icon="📦" title="Load stock valuation" subtitle="Pick a date and press Load" />
      ) : (
        <div style={{ background: "white", border: "1px solid #e2e7e2", borderRadius: "8px", overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #e2e7e2", background: "#fafbfa" }}>
            <h4 style={{ fontSize: "14px", fontWeight: "700", color: "#1e293b", margin: 0 }}>Valuation</h4>
            <span style={{ fontSize: "14px", fontWeight: "800", color: "#1e293b" }}>
              {data.totals ? `${fmt.moneyShort(data.totals.total_value)} · ${data.totals.total_qty} units` : ""}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th className="ess-th">Product</th>
                  <th className="ess-th">Category</th>
                  <th className="ess-th ess-th-r">Qty</th>
                  <th className="ess-th ess-th-r">Unit Cost</th>
                  <th className="ess-th ess-th-r">Value</th>
                </tr>
              </thead>
              <tbody>
                {(data.lines || []).map((l, i) => (
                  <tr key={i} className="ess-tr">
                    <td className="ess-td" style={{ color: "#475569" }}>
                      <strong>{l.product_name}</strong> <span style={{ color: "#94a3b8", fontFamily: "monospace" }}>{l.internal_ref}</span>
                    </td>
                    <td className="ess-td" style={{ color: "#64748b" }}>{l.category || "—"}</td>
                    <td className="ess-td" style={{ textAlign: "right", color: "#334155" }}>{fmt.qty(l.qty_on_hand)}</td>
                    <td className="ess-td" style={{ textAlign: "right", color: "#334155", whiteSpace: "nowrap" }}>{fmt.money(l.unit_cost)}</td>
                    <td className="ess-td" style={{ textAlign: "right", fontWeight: "600", whiteSpace: "nowrap" }}>{fmt.money(l.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminPanelReportsPage = () => {
  const [tab, setTab] = useState("pl");
  const tabs = [
    { key: "pl", label: "Profit & Loss" },
    { key: "bs", label: "Balance Sheet" },
    { key: "taxes", label: "Taxes / VAT" },
    { key: "sv", label: "Stock Valuation" },
  ];

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader title="Financial Reports" subtitle="Profit & Loss, Balance Sheet, Taxes and stock valuation — export to XLSX / PDF" />
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === "pl" && <PL />}
      {tab === "bs" && <BS />}
      {tab === "taxes" && <TX />}
      {tab === "sv" && <SV />}
    </div>
  );
};

export default AdminPanelReportsPage;
